import { NgxIndexedDBService } from 'ngx-indexed-db';
import { BehaviorSubject, Subject, filter, forkJoin, from, NEVER, Observable, of, take, throwError, timeout, timer } from 'rxjs';
import { catchError, concatMap, delay, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { ApiPath, ApiRequestOptions, ApiService } from '../../api';
import { InterceptorConfig } from '../../interceptors';
import { AnimalIdReconcilable, AnimalIdRemap, replaceAnimalId } from './animal-reference.reconciler';
import { NetworkStatusService } from './network-status.service';
import { MultipartPayload, SyncOperation, SyncQueueItem, SyncResult } from './sync-types';
import { Injectable } from '@angular/core';
import { base64ToBlob } from '../../../../../common-utils/src/lib/utils/file.utils';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HomeleiteTenant, TenantsRepository } from 'libs/domain/src/lib/tenant';
import { SyncStatusService } from './sync-status.service';

@Injectable({ providedIn: 'root' })
export class SynchronizationService {
    private isProcessingQueue = false;
    private duplicateAnimalNicknames: string[] = [];
    private readonly syncQueueStoreName = 'syncQueue';
    private tenant: HomeleiteTenant | null = null;
    private _syncing$ = new BehaviorSubject<boolean>(false);
    public syncing$ = this._syncing$.asObservable();
    private _queueProcessed$ = new Subject<void>();
    public queueProcessed$ = this._queueProcessed$.asObservable();

    /**
     * Fila “vazia” para disparar pull: sem itens com retry devido agora.
     * Itens `failed` ainda bloqueiam wipe de stores sensíveis via {@link hasDirtyStores}.
     */
    public isQueueEmpty = this.dbService.getAll<SyncQueueItem>(this.syncQueueStoreName)
        .pipe(
            map(items => {
                const now = Date.now();
                return (items ?? []).every(
                    (item) => !!item.failed && !!item.nextAttemptAt && item.nextAttemptAt > now
                );
            })
        );

    /** true se existir qualquer item (pending ou failed) nos stores informados. */
    public hasDirtyStores(storeNames: string[]): Observable<boolean> {
        const wanted = new Set(storeNames);
        return this.dbService.getAll<SyncQueueItem>(this.syncQueueStoreName).pipe(
            map((items) => (items ?? []).some((item) => wanted.has(item.storeName)))
        );
    }

    constructor(
        private apiService: ApiService,
        private dbService: NgxIndexedDBService,
        private networkStatusService: NetworkStatusService,
        private snackbar: MatSnackBar,
        private tenantsRepository: TenantsRepository,
        private syncStatus: SyncStatusService
    ) {
        this.setupReactiveSync();

        this.tenantsRepository.fetchDefault().pipe(take(1)).subscribe(tenant => {
            this.tenant = tenant;
        });
    }

    private readonly animalIdReconcilables: AnimalIdReconcilable[] = [];

    public registerAnimalIdReconciler(reconcilable: AnimalIdReconcilable): void {
        this.animalIdReconcilables.push(reconcilable);
    }

    public attemptSync<T>(operation: SyncOperation<T>, storeName: string): Observable<SyncResult> {
        if (this.networkStatusService.isOnline()) {
            const apiPath: ApiPath = this.buildApiPath(operation.resourcePath, operation.resourceId);
            const requestOptions: ApiRequestOptions = this.requestOptions(operation.method, storeName);
            let apiRequest$: Observable<any>;

            switch (operation.method) {
                case 'POST':
                    if (operation.isMultipart) {
                        const formData = new FormData();
                        const payload = operation.payload as any;

                        if (payload && payload.filePart && payload.jsonPart) {
                            const mpPayload = payload as MultipartPayload;

                            const imageBlob = base64ToBlob(mpPayload.filePart.base64Data);

                            formData.append('file', imageBlob, mpPayload.filePart.filename);
                            formData.append('animal', JSON.stringify(mpPayload.jsonPart));
                        }

                        apiRequest$ = apiPath.postMultipartForm(formData, requestOptions);
                    } else {
                        apiRequest$ = apiPath.postJson(operation.payload, requestOptions);
                    }
                    break;
                case 'PUT':
                    if (operation.isMultipart && operation.payload instanceof File) {
                        const formData = new FormData();
                        formData.append('file', operation.payload, operation.payload.name);
                        apiRequest$ = apiPath.putMultipartForm(formData);
                    } else {
                        apiRequest$ = apiPath.putJson(operation.payload);
                    }
                    break;
                case 'DELETE':
                    apiRequest$ = apiPath.delete();
                    break;
                default:
                    return throwError(() => new Error(`Unsupported sync method: ${operation.method}`));
            }

            return apiRequest$.pipe(
                map(response => ({
                    operation, success: true, queued: false, response
                } satisfies SyncResult)),
                catchError((error) => {
                    return this.addToQueueInternal(operation, storeName, error).pipe(
                        map(queueResult => ({
                            operation, success: false, queued: true, error
                        } satisfies SyncResult))
                    );
                })
            );
        } else {
            return this.addToQueueInternal(operation, storeName).pipe(
                map(queueResult => ({
                    operation, success: false, queued: true
                } satisfies SyncResult))
            );
        }
    }

    public processSyncQueue(): void {
        if (!this.isQueueEnabled() || this.isProcessingQueue || !this.networkStatusService.isOnline()) {
            return;
        }

        // Só marca "processando" depois de saber que há itens devidos —
        // evita toast/overlay ao abrir o app sem pendência real.
        this.isProcessingQueue = true;
        this.syncStatus.beginScan();

        this.dbService.getAll<SyncQueueItem>(this.syncQueueStoreName).pipe(
            mergeMap(queueItems => {
                const now = Date.now();
                queueItems = (queueItems ?? []).filter(item => !item.nextAttemptAt || item.nextAttemptAt <= now);
                if (queueItems.length === 0) {
                    this.isProcessingQueue = false;
                    this._syncing$.next(false);
                    this.syncStatus.reset();
                    return NEVER;
                }

                this._syncing$.next(true);
                this.syncStatus.startProcessing(queueItems.length);

                const sortedItems = queueItems.sort((a, b) => a.timestamp - b.timestamp);

                return from(sortedItems).pipe(
                    concatMap(item => {
                        return this.dbService.getByKey<SyncQueueItem>(this.syncQueueStoreName, item.id!).pipe(
                            concatMap(freshItem => {
                                return freshItem
                                    ? this.processSingleQueueItem(freshItem)
                                    : of(true);
                            }),
                            tap((ok) => this.syncStatus.markItemDone(!!ok)),
                            delay(800)
                        );
                    })
                );
            }),
            catchError(error => {
                console.error('Erro no processamento da fila de sincronização', error);
                return of([]);
            })
        ).subscribe({
            next: () => {},
            error: err => {
                console.error('Erro fila de sincronização', err);
                this.isProcessingQueue = false;
                this.duplicateAnimalNicknames = [];
                this._syncing$.next(false);
                this.syncStatus.reset();
            },
            complete: () => {
                if (this.duplicateAnimalNicknames.length > 0) {
                    this.showSnackbar(
                        `Animais que já estavam cadastrados e foram vinculados: ${this.duplicateAnimalNicknames.join(', ')}.`,
                        8000
                    );
                    this.duplicateAnimalNicknames = [];
                }
                this.syncStatus.finish();
                this.isProcessingQueue = false;
                this._syncing$.next(false);
                this._queueProcessed$.next();
            }
        });
    }

    private processSingleQueueItem(item: SyncQueueItem): Observable<boolean> {
        const apiPath: ApiPath = this.buildApiPath(item.resourcePath, item.resourceId);
        const requestOptions: ApiRequestOptions = this.requestOptions(item.method, item.storeName);
        let apiRequest$: Observable<any>;

        switch (item.method) {
            case 'POST':
                if (item.isMultipart) {
                    const formData = new FormData();
                    const payload = item.payload as any;

                    if (payload && payload.filePart && payload.jsonPart) {
                        const mpPayload = payload as MultipartPayload;

                        const imageBlob = base64ToBlob(mpPayload.filePart.base64Data);

                        formData.append('file', imageBlob, mpPayload.filePart.filename);
                        formData.append('animal', JSON.stringify(mpPayload.jsonPart));
                    }
                    apiRequest$ = apiPath.postMultipartForm(formData, requestOptions);
                } else {
                    apiRequest$ = apiPath.postJson(item.payload, requestOptions);
                }
                break;
            case 'PUT':
                if (item.isMultipart && item.payload instanceof File) {
                    const formData = new FormData();
                    formData.append('file', item.payload, item.payload.name);
                    apiRequest$ = apiPath.putMultipartForm(formData);
                } else {
                    apiRequest$ = apiPath.putJson(item.payload);
                }
                break;
            case 'DELETE':
                apiRequest$ = apiPath.delete();
                break;
            default:
                console.warn(`[SYNC] Método não suportado: ${item.method}`);
                return of(true);
        }

        return apiRequest$.pipe(
            timeout(30000),
            switchMap(() => this.deleteQueueItem(item)),
            catchError(error => {
                const isAnimalCreation = item.method === 'POST' && item.storeName === 'animals';
                const returnedAnimal = error?.status === 409 ? error.error?.error : null;

                if (isAnimalCreation && typeof returnedAnimal?.id === 'string' && returnedAnimal.id) {
                    return this.reconcileDuplicateAnimal(item, returnedAnimal);
                }

                return this.retryOrRegisterFailure(item, error, error?.status >= 500 || error?.status === 0);
            })
        );
    }

    private reconcileDuplicateAnimal(item: SyncQueueItem, returnedAnimal: any): Observable<boolean> {
        const localAnimal = item.isMultipart ? (item.payload as MultipartPayload).jsonPart : item.payload;
        const remap: AnimalIdRemap = { localAnimalId: localAnimal?.id, returnedAnimalId: returnedAnimal.id, returnedAnimal };

        if (!remap.localAnimalId || remap.localAnimalId === remap.returnedAnimalId) {
            return this.deleteQueueItem(item);
        }

        return this.dbService.update(item.storeName, returnedAnimal).pipe(
            switchMap(() => this.dbService.deleteByKey(item.storeName, remap.localAnimalId)),
            switchMap(() => this.remapQueueReferences(item.id!, remap)),
            switchMap(() => this.cascadeAnimalId(remap)),
            switchMap(() => this.deleteQueueItem(item)),
            tap(() => this.duplicateAnimalNicknames.push(localAnimal.nickname)),
            catchError(error => {
                console.error(`Falha na troca de referência do animal do item ${item.id} (${remap.localAnimalId} → ${remap.returnedAnimalId}), item segue para retry ou registro de falha`, error);
                return this.retryOrRegisterFailure(item, error, true);
            })
        );
    }

    private cascadeAnimalId(remap: AnimalIdRemap): Observable<unknown> {
        const reconciles = this.animalIdReconcilables.map(reconcilable => reconcilable.reconcileAnimalId(remap));
        return reconciles.length ? forkJoin(reconciles) : of(null);
    }

    private remapQueueReferences(currentItemId: number, remap: AnimalIdRemap): Observable<unknown> {
        return this.dbService.getAll<SyncQueueItem>(this.syncQueueStoreName).pipe(
            map(items => items
                .filter(queueItem => queueItem.id !== currentItemId && JSON.stringify(queueItem).includes(remap.localAnimalId))
                .map(queueItem => replaceAnimalId(queueItem, remap) as SyncQueueItem)),
            switchMap(items => items.length
                ? this.dbService.bulkPut(this.syncQueueStoreName, items)
                : of(null))
        );
    }

    private static readonly MAX_FAST_RETRIES = 5;
    private static readonly SLOW_RETRY_MS = 6 * 60 * 60 * 1000;

    private static backoffMs(attempt: number): number {
        const base = Math.min(5000 * 2 ** attempt, 60 * 60 * 1000);
        return Math.round(base * (0.5 + Math.random() * 0.5));
    }

    private retryOrRegisterFailure(item: SyncQueueItem, cause: any, shouldRetry: boolean): Observable<boolean> {
        const status: number | undefined = cause?.status;
        const permanent = !shouldRetry && status >= 400 && status < 500 && ![401, 403, 408, 429].includes(status);

        item.lastError = String(cause?.error?.message ?? cause?.message ?? 'Erro desconhecido').slice(0, 300);
        item.lastStatus = status;
        item.lastAttemptAt = Date.now();
        item.retryCount++;
        item.failed = permanent || item.retryCount >= SynchronizationService.MAX_FAST_RETRIES;
        item.nextAttemptAt = Date.now() + (item.failed
            ? SynchronizationService.SLOW_RETRY_MS
            : SynchronizationService.backoffMs(item.retryCount));

        if (!item.failed || item.failureReported) {
            return this.dbService.update(this.syncQueueStoreName, item).pipe(map(() => false));
        }

        const failureLogPayload = {
            id: crypto.randomUUID(),
            tenantId: this.tenant?.id,
            payload: { raw: item.payload, resourcePath: item.resourcePath },
            error: {
                raw: { status, message: cause?.message, url: cause?.url, name: cause?.name, full: cause }
            },
            resourcePath: item.resourcePath,
        };

        return this.buildApiPath('application-logs/sync-failures').postJson(failureLogPayload).pipe(
            tap(() => {
                item.failureReported = true;
                this.showSnackbar(`Item ${item.id} não sincronizou após várias tentativas. Segue guardado e será reenviado automaticamente.`);
            }),
            catchError(err => {
                console.error('Falha ao registrar sync-failure', err);
                return of(null);
            }),
            switchMap(() => this.dbService.update(this.syncQueueStoreName, item)),
            map(() => false)
        );
    }

    private deleteQueueItem(item: SyncQueueItem): Observable<boolean> {
        return this.dbService.delete(this.syncQueueStoreName, item.id!).pipe(map(() => true));
    }

    public getPendingItems(): Observable<SyncQueueItem[]> {
        return this.dbService.getAll<SyncQueueItem>(this.syncQueueStoreName);
    }

    private addToQueueInternal<T>(operation: SyncOperation<T>, storeName: string, errorInfo?: any): Observable<SyncQueueItem> {
        const item: SyncQueueItem = {
            ...operation,
            storeName: storeName,
            timestamp: Date.now(),
            retryCount: 0
        };
        return this.dbService.add<SyncQueueItem>(this.syncQueueStoreName, item);
    }

    private requestOptions(method: string, storeName: string): ApiRequestOptions {
        return method === 'POST' && storeName === 'animals'
            ? { metadata: { autoCatch: InterceptorConfig.NO_INTERCEPT } }
            : {};
    }

    private buildApiPath(resourcePath: string, resourceId?: string): ApiPath {
         if (resourceId) {
            return this.apiService.root.path(resourcePath, resourceId);
        } else {
            return this.apiService.root.path(resourcePath);
        }
    }

    private setupReactiveSync(): void {
        // Fila offline é do PWA. No admin (mesmo browser/IndexedDB) não processar.
        // UI de progresso: SyncStatusService (banner), sem snackbar rotineiro.
        if (!this.isQueueEnabled()) {
            return;
        }

        this.networkStatusService.isOnline$()
            .pipe(
                filter(isOnline => isOnline),
                switchMap(() => timer(2000))
            )
            .subscribe(() => {
                this.processSyncQueue();
            });

        timer(60000, 60000)
            .pipe(
                filter(() => this.networkStatusService.isOnline()),
                switchMap(() => this.dbService.count(this.syncQueueStoreName)),
                filter(count => count > 0)
            )
            .subscribe({
                next: () => this.processSyncQueue(),
                error: err => console.error(err)
            });
    }

    private isQueueEnabled(): boolean {
        try {
            const host = location.hostname || '';
            if (host.includes('admin')) {
                return false;
            }
            // build admin local / paths
            if (location.pathname?.includes('/admin')) {
                return false;
            }
        } catch {
            // sem location: deixa ativo (PWA)
        }
        return true;
    }

    private showSnackbar(message: string, duration = 4000): void {
        this.snackbar.open(
            message,
            'Fechar', {
                duration,
                panelClass: [`custom-snackbar`],
                verticalPosition: 'top'
            }
        );
    }
}
